/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react'
import { connectionManager } from '../services/connectionManager'
import { logger } from '../../utils/logger'

interface ConnectionContextValue {
  isConnected: boolean
  channelCount: number
  lastConnectedAt: Date | null
  connectionUptime: number
}

const ConnectionContext = createContext<ConnectionContextValue | undefined>(undefined)

interface ConnectionProviderProps {
  children: ReactNode
}

export function ConnectionProvider({ children }: ConnectionProviderProps) {
  const [isConnected, setIsConnected] = useState(connectionManager.isRealtimeConnected())
  const [channelCount, setChannelCount] = useState(connectionManager.getChannelCount())
  const [lastConnectedAt, setLastConnectedAt] = useState<Date | null>(null)
  const [connectionUptime, setConnectionUptime] = useState(0)

  const lastConnectedAtRef = useRef<Date | null>(null)
  const isConnectedRef = useRef(false)
  const wasEverConnectedRef = useRef(false)

  useEffect(() => {
    logger.info('ConnectionProvider initialized', { component: 'ConnectionProvider' })

    const unsubscribe = connectionManager.addListener((connected) => {
      const previouslyConnected = isConnectedRef.current
      isConnectedRef.current = connected
      setIsConnected(connected)
      setChannelCount(connectionManager.getChannelCount())

      if (connected) {
        wasEverConnectedRef.current = true
        const now = new Date()
        lastConnectedAtRef.current = now
        setLastConnectedAt(now)
        logger.info('Connection established via ConnectionContext', {
          component: 'ConnectionProvider'
        })
      } else if (wasEverConnectedRef.current && previouslyConnected) {
        logger.warn('Connection lost via ConnectionContext', {
          component: 'ConnectionProvider'
        })
      }
    })

    const uptimeInterval = setInterval(() => {
      if (isConnectedRef.current && lastConnectedAtRef.current) {
        const uptime = Math.floor((Date.now() - lastConnectedAtRef.current.getTime()) / 1000)
        setConnectionUptime(uptime)
      } else {
        setConnectionUptime(0)
      }
    }, 1000)

    return () => {
      unsubscribe()
      clearInterval(uptimeInterval)
      logger.info('ConnectionProvider cleanup', { component: 'ConnectionProvider' })
    }
  }, [])

  const value: ConnectionContextValue = {
    isConnected,
    channelCount,
    lastConnectedAt,
    connectionUptime
  }

  return (
    <ConnectionContext.Provider value={value}>
      {children}
    </ConnectionContext.Provider>
  )
}

export function useConnection(): ConnectionContextValue {
  const context = useContext(ConnectionContext)
  
  if (context === undefined) {
    throw new Error('useConnection must be used within a ConnectionProvider')
  }
  
  return context
}
